/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { inject, Injectable } from '@angular/core';
import { AlfrescoApiService } from '@alfresco/adf-content-services';
import { NodesApi } from '@alfresco/js-api';
import { from, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

export interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  modifiedAt?: string;
  sizeInBytes?: number | null;
}

/** Placeholders the templates support (filled in by the server-side expiry job). */
export const EMAIL_TEMPLATE_PLACEHOLDERS = ['{name}', '{path}', '{expiryDate}', '{owner}', '{modifier}'];

/**
 * CRUD for email templates used by the document-expiry notifications. Each template
 * is a content node under "/Company Home/Email Templates": the subject is stored in
 * `cm:title` and the body is the node content. The server-side scheduled job reads
 * these same nodes, so templates created here are reused when sending mail.
 */
@Injectable({ providedIn: 'root' })
export class EmailTemplateService {
  private readonly api = inject(AlfrescoApiService);
  private static readonly FOLDER = 'Email Templates';

  private get nodes(): NodesApi {
    return new NodesApi(this.api.getInstance() as any);
  }

  private folderId(): Observable<string> {
    return this.existingFolderId().pipe(
      switchMap((id) =>
        id
          ? of(id)
          : from(this.nodes.createNode('-root-', { name: EmailTemplateService.FOLDER, nodeType: 'cm:folder' } as any) as Promise<any>).pipe(
              map((res) => res.entry.id)
            )
      )
    );
  }

  /** The folder id if it is already there, without creating it. */
  private existingFolderId(): Observable<string | null> {
    return from(this.nodes.getNode('-root-', { relativePath: EmailTemplateService.FOLDER }) as Promise<any>).pipe(
      map((res) => res.entry.id as string),
      catchError(() => of(null))
    );
  }

  list(): Observable<EmailTemplate[]> {
    return this.existingFolderId().pipe(
      switchMap((fid) =>
        fid ? from(this.nodes.listNodeChildren(fid, { include: ['properties'], maxItems: 200 } as any) as Promise<any>) : of(null)
      ),
      map((res: any) =>
        (res?.list?.entries || [])
          .map((e: any) => e.entry)
          .filter((n: any) => n.isFile)
          .map(
            (n: any): EmailTemplate => ({
              id: n.id,
              name: n.name,
              subject: n.properties?.['cm:title'] || '',
              modifiedAt: n.modifiedAt,
              sizeInBytes: n.content?.sizeInBytes ?? null
            })
          )
      ),
      catchError(() => of([] as EmailTemplate[]))
    );
  }

  /** Reads the template body (node content) as text. */
  getBody(id: string): Observable<string> {
    return from(this.nodes.getNodeContent(id) as Promise<any>).pipe(
      switchMap((data: any) => from(typeof data?.text === 'function' ? (data.text() as Promise<string>) : Promise.resolve(String(data ?? ''))))
    );
  }

  create(name: string, subject: string, body: string): Observable<any> {
    return this.folderId().pipe(
      switchMap((fid) =>
        from(this.nodes.createNode(fid, { name, nodeType: 'cm:content', properties: { 'cm:title': subject } } as any) as Promise<any>)
      ),
      switchMap((res: any) => from(this.nodes.updateNodeContent(res.entry.id, body) as Promise<any>))
    );
  }

  update(id: string, subject: string, body: string): Observable<any> {
    return from(this.nodes.updateNode(id, { properties: { 'cm:title': subject } } as any) as Promise<any>).pipe(
      switchMap(() => from(this.nodes.updateNodeContent(id, body) as Promise<any>))
    );
  }

  delete(id: string): Observable<any> {
    return from(this.nodes.deleteNode(id, { permanent: true } as any) as Promise<any>);
  }
}
